import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Engineering as EngineeringIcon,
  DateRange as DateRangeIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Devices as DevicesIcon,
  TrendingUp as TrendingUpIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  getAllTechnicians,
  getTechnicianStats,
  getTechnicianEarnings
} from '../services/technicianService';
import { getMaintenanceByDateRangeAndBranch } from '../services/maintenanceService';
import { getAllBranches } from '../services/branchService';
import { Technician, TechnicianStats, TechnicianEarning, TechnicianSalaryType, MaintenanceRecord, Branch } from '../utils/types';
import { useCurrency } from '../hooks/useCurrency';

// نوع الفترة الزمنية
type TimeRange = 'today' | 'week' | 'month' | 'year' | 'custom';

// نوع بيانات ربط الجهاز بالفني
interface DeviceTechnicianLink {
  maintenanceId: string;
  orderNumber: number;
  deviceType: string;
  deviceSize: string;
  customerName: string;
  technicianId: string;
  technicianName: string;
  salaryType: TechnicianSalaryType;
  baseAmount: number;
  earningAmount: number;
  calculationDetails: string;
  status: string;
  createdDate: Date;
  completedDate?: Date;
  branchName: string;
}

const TechnicianDeviceReportPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<'orderNumber' | 'customerName' | 'earningAmount' | 'createdDate'>('orderNumber');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [timeRange, setTimeRange] = useState<TimeRange>('month');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [deviceLinks, setDeviceLinks] = useState<DeviceTechnicianLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // إحصائيات
  const [totalDevices, setTotalDevices] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [completedDevices, setCompletedDevices] = useState(0);
  const [pendingDevices, setPendingDevices] = useState(0);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadReportData();
  }, [selectedTechnician, selectedBranch, statusFilter, timeRange, customStartDate, customEndDate]);

  const loadInitialData = async () => {
    try {
      console.log('🔄 بدء تحميل البيانات الأولية...');
      setLoadingInitial(true);
      setError(null);

      // تحميل الفنيين
      console.log('👨‍🔧 تحميل قائمة الفنيين...');
      const techniciansList = await getAllTechnicians(true);
      console.log('✅ تم تحميل الفنيين:', techniciansList.length);
      setTechnicians(techniciansList);

      // تحميل الفروع
      console.log('🏢 تحميل قائمة الفروع...');
      const branchesList = await getAllBranches();
      console.log('✅ تم تحميل الفروع:', branchesList.length);
      setBranches(branchesList);

      console.log('✅ تم تحميل جميع البيانات الأولية بنجاح');
    } catch (error) {
      console.error('❌ خطأ في تحميل البيانات الأولية:', error);
      console.error('تفاصيل الخطأ:', {
        message: error instanceof Error ? error.message : 'خطأ غير معروف',
        stack: error instanceof Error ? error.stack : undefined
      });
      setError(`فشل في تحميل البيانات الأولية: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
    } finally {
      setLoadingInitial(false);
    }
  };

  const getDateRange = (): { startDate: Date; endDate: Date } => {
    const now = new Date();
    let endDate = new Date(now);
    let startDate = new Date(now);

    switch (timeRange) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        }
        break;
    }

    return { startDate, endDate };
  };

  const loadReportData = async () => {
    try {
      console.log('📊 بدء تحميل بيانات التقرير...');
      setLoading(true);
      setError(null);

      const { startDate, endDate } = getDateRange();
      console.log('📅 الفترة المحددة:', { startDate, endDate });
      console.log('🏢 الفرع المحدد:', selectedBranch);
      console.log('👨‍🔧 الفني المحدد:', selectedTechnician);

      // جلب طلبات الصيانة في الفترة المحددة
      console.log('🔍 جلب طلبات الصيانة...');
      const maintenanceRecords = await getMaintenanceByDateRangeAndBranch(
        startDate,
        endDate,
        selectedBranch === 'all' ? undefined : selectedBranch
      );
      console.log('📋 عدد طلبات الصيانة:', maintenanceRecords.length);

      // تصفية الطلبات التي لها فني مرتبط
      const recordsWithTechnicians = maintenanceRecords.filter(record =>
        record.technicianId &&
        (selectedTechnician === 'all' || record.technicianId === selectedTechnician) &&
        (statusFilter === 'all' || record.status === statusFilter)
      );
      console.log('👨‍🔧 طلبات الصيانة المربوطة بفنيين:', recordsWithTechnicians.length);

      // جلب أجور الفنيين للطلبات المكتملة
      const deviceLinksPromises = recordsWithTechnicians.map(async (record) => {
        console.log(`🔍 معالجة الطلب ${record.orderNumber}:`, {
          technicianId: record.technicianId,
          technicianName: record.technicianName,
          availableTechnicians: technicians.length
        });

        let technician = technicians.find(t => t.id === record.technicianId);

        // إذا لم نجد الفني في القائمة المحملة، نحاول جلبه من قاعدة البيانات
        if (!technician && record.technicianId) {
          try {
            console.log(`🔍 محاولة جلب بيانات الفني ${record.technicianId} من قاعدة البيانات`);
            const { getTechnicianById } = await import('../services/technicianService');
            const fetchedTechnician = await getTechnicianById(record.technicianId);
            if (fetchedTechnician) {
              technician = fetchedTechnician;
            }
            console.log(`✅ تم جلب بيانات الفني:`, technician?.name);
          } catch (error) {
            console.warn(`⚠️ فشل في جلب بيانات الفني ${record.technicianId}:`, error);
          }
        }

        const branch = branches.find(b => b.id === record.branchId);
        
        let earningAmount = 0;
        let calculationDetails = 'لم يتم الحساب بعد';

        // حساب الأجر للطلبات المكتملة أو المسلمة
        const isCompleted = record.status === 'completed' || record.status === 'delivered';

        if (isCompleted && technician) {
          // حساب الأجر
          const baseAmount = record.finalCost || record.initialCost || 0;
          console.log(`💰 حساب أجر الفني ${technician.name} للطلب ${record.orderNumber}:`, {
            status: record.status,
            salaryType: technician.salaryType,
            baseAmount,
            salaryAmount: technician.salaryAmount
          });
          
          switch (technician.salaryType) {
            case TechnicianSalaryType.FIXED:
              earningAmount = technician.salaryAmount;
              calculationDetails = `راتب ثابت: ${technician.salaryAmount} جنيه`;
              console.log(`✅ راتب ثابت محسوب: ${earningAmount} جنيه`);
              break;
            case TechnicianSalaryType.PER_DEVICE:
              earningAmount = technician.salaryAmount;
              calculationDetails = `أجر بالقطعة: ${technician.salaryAmount} جنيه`;
              console.log(`✅ أجر بالقطعة محسوب: ${earningAmount} جنيه`);
              break;
            case TechnicianSalaryType.PERCENTAGE:
              earningAmount = (baseAmount * technician.salaryAmount) / 100;
              calculationDetails = `نسبة ${technician.salaryAmount}% من ${baseAmount} = ${earningAmount.toFixed(2)} جنيه`;
              console.log(`✅ نسبة مئوية محسوبة: ${earningAmount} جنيه`);
              break;
            case TechnicianSalaryType.VARIABLE:
              // للمبلغ المتغير، نحاول جلب المبلغ المحفوظ من قاعدة البيانات أولاً
              try {
                console.log(`🔍 البحث عن المبلغ المحفوظ للطلب ${record.id} والفني ${technician.id}`);

                // محاولة جلب المبلغ المخصص من سجلات أجور الفنيين
                const { getTechnicianEarnings } = await import('../services/technicianService');

                // توسيع نطاق البحث للتأكد من العثور على السجل
                const searchStartDate = new Date(record.receivedAt);
                searchStartDate.setDate(searchStartDate.getDate() - 7); // أسبوع قبل تاريخ الاستلام
                const searchEndDate = new Date();
                searchEndDate.setDate(searchEndDate.getDate() + 1); // يوم إضافي للتأكد

                const earnings = await getTechnicianEarnings(technician.id, searchStartDate, searchEndDate);
                console.log(`📋 عدد سجلات الأجور الموجودة: ${earnings.length}`);

                if (earnings.length > 0) {
                  console.log(`📋 سجلات الأجور الموجودة:`, earnings.map(e => ({
                    maintenanceId: e.maintenanceId,
                    earningAmount: e.earningAmount,
                    date: e.date
                  })));
                }

                // البحث عن السجل المطابق لهذا الطلب
                const matchingEarning = earnings.find(earning => earning.maintenanceId === record.id);

                if (matchingEarning) {
                  earningAmount = matchingEarning.earningAmount;
                  calculationDetails = `مبلغ متغير محفوظ: ${earningAmount} جنيه`;
                  console.log(`✅ تم العثور على مبلغ محفوظ: ${earningAmount} جنيه`);
                } else {
                  console.log(`ℹ️ لم يتم العثور على سجل محفوظ للطلب ${record.id}، حساب مبلغ افتراضي`);

                  // إذا لم يوجد سجل محفوظ، نحسب مبلغ افتراضي بناءً على التكلفة
                  if (baseAmount > 0) {
                    // حساب نسبة من التكلفة (15% كافتراضي)
                    earningAmount = baseAmount * 0.15;
                    calculationDetails = `مبلغ متغير (15% من التكلفة ${baseAmount}): ${earningAmount.toFixed(2)} جنيه`;
                  } else if (technician.salaryAmount > 0) {
                    // استخدام المبلغ الافتراضي للفني
                    earningAmount = technician.salaryAmount;
                    calculationDetails = `مبلغ متغير (افتراضي للفني): ${earningAmount} جنيه`;
                  } else {
                    // مبلغ افتراضي عام
                    earningAmount = 100;
                    calculationDetails = `مبلغ متغير (افتراضي عام): ${earningAmount} جنيه`;
                  }
                }
              } catch (error) {
                console.warn('⚠️ خطأ في جلب أجور الفني:', error);

                // في حالة الخطأ، نحسب مبلغ افتراضي بناءً على التكلفة
                if (baseAmount > 0) {
                  earningAmount = baseAmount * 0.15; // 15% من التكلفة
                  calculationDetails = `مبلغ متغير (احتياطي - 15% من التكلفة): ${earningAmount.toFixed(2)} جنيه`;
                } else if (technician.salaryAmount > 0) {
                  earningAmount = technician.salaryAmount;
                  calculationDetails = `مبلغ متغير (احتياطي - افتراضي للفني): ${earningAmount} جنيه`;
                } else {
                  earningAmount = 100;
                  calculationDetails = `مبلغ متغير (احتياطي - عام): ${earningAmount} جنيه`;
                }
              }

              console.log(`✅ المبلغ النهائي للمتغير: ${earningAmount} جنيه - ${calculationDetails}`);
              break;
            default:
              console.warn(`⚠️ نوع راتب غير معروف: ${technician.salaryType}`);
              break;
          }
        } else if (!isCompleted) {
          console.log(`ℹ️ الطلب ${record.orderNumber} غير مكتمل (الحالة: ${record.status})`);
          calculationDetails = `الطلب غير مكتمل (${record.status})`;
        } else if (!technician) {
          console.warn(`⚠️ لم يتم العثور على بيانات الفني ${record.technicianId} للطلب ${record.orderNumber}`);
          // استخدام بيانات الفني من السجل إذا لم نجد الفني في قاعدة البيانات
          if (record.technicianName) {
            calculationDetails = `فني: ${record.technicianName} (بيانات محدودة)`;
          } else {
            calculationDetails = 'بيانات الفني غير متوفرة';
          }
        }

        // تحديد نوع الراتب بناءً على المعلومات المتاحة
        let salaryType = technician?.salaryType;
        if (!salaryType) {
          // إذا لم نجد بيانات الفني، نحاول تخمين النوع من تفاصيل الحساب
          if (calculationDetails.includes('متغير')) {
            salaryType = TechnicianSalaryType.VARIABLE;
          } else if (calculationDetails.includes('نسبة')) {
            salaryType = TechnicianSalaryType.PERCENTAGE;
          } else if (calculationDetails.includes('قطعة')) {
            salaryType = TechnicianSalaryType.PER_DEVICE;
          } else {
            salaryType = TechnicianSalaryType.FIXED;
          }
        }

        return {
          maintenanceId: record.id,
          orderNumber: record.orderNumber,
          deviceType: record.deviceType || 'غير محدد',
          deviceSize: record.devices?.[0]?.size || '',
          customerName: record.customerName,
          technicianId: record.technicianId!,
          technicianName: record.technicianName!,
          salaryType,
          baseAmount: record.finalCost || record.initialCost || 0,
          earningAmount,
          calculationDetails,
          status: record.status,
          createdDate: new Date(record.receivedAt || new Date()),
          completedDate: record.status === 'completed' && record.actualDeliveryDate
            ? new Date(record.actualDeliveryDate)
            : undefined,
          branchName: branch?.name || 'غير محدد'
        } as DeviceTechnicianLink;
      });

      const deviceLinksData = await Promise.all(deviceLinksPromises);
      setDeviceLinks(deviceLinksData);

      // حساب الإحصائيات
      setTotalDevices(deviceLinksData.length);

      const completedCount = deviceLinksData.filter(link =>
        link.status === 'completed' || link.status === 'delivered'
      ).length;
      setCompletedDevices(completedCount);

      const pendingCount = deviceLinksData.filter(link =>
        link.status !== 'completed' && link.status !== 'delivered'
      ).length;
      setPendingDevices(pendingCount);

      const totalEarnings = deviceLinksData
        .filter(link => link.status === 'completed' || link.status === 'delivered')
        .reduce((sum, link) => sum + link.earningAmount, 0);
      setTotalEarnings(totalEarnings);

      console.log('📊 إحصائيات التقرير:', {
        totalDevices: deviceLinksData.length,
        completedDevices: completedCount,
        pendingDevices: pendingCount,
        totalEarnings
      });

    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات التقرير:', error);
      console.error('تفاصيل الخطأ:', {
        message: error instanceof Error ? error.message : 'خطأ غير معروف',
        stack: error instanceof Error ? error.stack : undefined,
        selectedTechnician,
        selectedBranch,
        timeRange
      });
      setError(`فشل في تحميل بيانات التقرير: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
    } finally {
      setLoading(false);
    }
  };

  const getSalaryTypeLabel = (type: TechnicianSalaryType): string => {
    switch (type) {
      case TechnicianSalaryType.FIXED:
        return 'راتب ثابت';
      case TechnicianSalaryType.PER_DEVICE:
        return 'بالقطعة';
      case TechnicianSalaryType.PERCENTAGE:
        return 'بالنسبة المئوية';
      case TechnicianSalaryType.VARIABLE:
        return 'قطعة متغيرة';
      default:
        return 'غير محدد';
    }
  };

  const getSalaryTypeColor = (type: TechnicianSalaryType): 'default' | 'primary' | 'secondary' | 'success' | 'warning' => {
    switch (type) {
      case TechnicianSalaryType.FIXED:
        return 'primary';
      case TechnicianSalaryType.PER_DEVICE:
        return 'success';
      case TechnicianSalaryType.PERCENTAGE:
        return 'warning';
      case TechnicianSalaryType.VARIABLE:
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'في الانتظار';
      case 'in_progress':
        return 'قيد التنفيذ';
      case 'completed':
        return 'مكتمل';
      case 'delivered':
        return 'تم التسليم';
      case 'cancelled':
        return 'مرتجع';
      case 'returned':
        return 'مرتجع';
      default:
        return status;
    }
  };

  const getStatusTooltip = (status: string): string => {
    switch (status) {
      case 'pending':
        return 'الطلب في انتظار بدء الصيانة';
      case 'in_progress':
        return 'جاري العمل على إصلاح الجهاز';
      case 'completed':
        return 'تم إصلاح الجهاز وهو جاهز للتسليم';
      case 'delivered':
        return 'تم تسليم الجهاز للعميل';
      case 'cancelled':
        return 'في انتظار الصيانة - تم إرجاع الجهاز قبل البدء';
      case 'refunded':
        return 'في فترة ضمان الصيانة - تم إرجاع الجهاز مع استرداد المبلغ';
      case 'returned':
        return 'تعدى مدة الضمان - تم إرجاع الجهاز مع رفض الإصلاح';
      default:
        return getStatusLabel(status);
    }
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'primary';
      case 'completed':
        return 'success';
      case 'delivered':
        return 'success';
      case 'returned':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTimeRangeLabel = (): string => {
    switch (timeRange) {
      case 'today':
        return 'اليوم';
      case 'week':
        return 'آخر أسبوع';
      case 'month':
        return 'آخر شهر';
      case 'year':
        return 'آخر سنة';
      case 'custom':
        return 'فترة مخصصة';
      default:
        return '';
    }
  };

  // إنشاء تقرير PDF
  const generatePDFReport = () => {
    try {
      console.log('📄 بدء إنشاء تقرير PDF...');

      const doc = new jsPDF();

      // إعداد الخط العربي (إذا كان متاحاً)
      doc.setFont('helvetica');

      // عنوان التقرير
      doc.setFontSize(20);
      doc.text('Technician Device Report', 105, 20, { align: 'center' });

      // معلومات التقرير
      doc.setFontSize(12);
      const reportInfo = [
        `Report Date: ${new Date().toLocaleDateString('en-US')}`,
        `Time Range: ${getTimeRangeLabel()}`,
        `Selected Technician: ${selectedTechnician === 'all' ? 'All Technicians' :
          technicians.find(t => t.id === selectedTechnician)?.name || 'Unknown'}`,
        `Selected Branch: ${selectedBranch === 'all' ? 'All Branches' :
          branches.find(b => b.id === selectedBranch)?.name || 'Unknown'}`
      ];

      let yPosition = 35;
      reportInfo.forEach(info => {
        doc.text(info, 20, yPosition);
        yPosition += 7;
      });

      // إحصائيات التقرير
      yPosition += 10;
      doc.setFontSize(14);
      doc.text('Report Statistics:', 20, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      const stats = [
        `Total Devices: ${totalDevices}`,
        `Completed Devices: ${completedDevices}`,
        `Pending Devices: ${pendingDevices}`,
        `Total Earnings: ${formatCurrency(totalEarnings)}`
      ];

      stats.forEach(stat => {
        doc.text(stat, 25, yPosition);
        yPosition += 7;
      });

      // جدول البيانات
      if (deviceLinks.length > 0) {
        yPosition += 10;

        const tableColumns = [
          'Order #',
          'Customer',
          'Device Type',
          'Technician',
          'Salary Type',
          'Base Amount',
          'Earning Amount',
          'Status',
          'Branch'
        ];

        const tableRows = deviceLinks.map(link => [
          `#${link.orderNumber}`,
          link.customerName,
          link.deviceType + (link.deviceSize ? ` (${link.deviceSize})` : ''),
          link.technicianName,
          getSalaryTypeLabel(link.salaryType),
          formatCurrency(link.baseAmount),
          formatCurrency(link.earningAmount),
          getStatusLabel(link.status),
          link.branchName
        ]);

        // استخدام jspdf-autotable لإنشاء الجدول
        (doc as any).autoTable({
          head: [tableColumns],
          body: tableRows,
          startY: yPosition,
          styles: {
            fontSize: 8,
            cellPadding: 2,
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245]
          },
          margin: { top: 20, right: 20, bottom: 20, left: 20 },
        });

        // إضافة إجمالي المبلغ في النهاية
        const finalY = (doc as any).lastAutoTable.finalY + 20;
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Total Amount Due: ${formatCurrency(totalEarnings)}`, 20, finalY);
      }

      // حفظ الملف
      const fileName = `technician-device-report-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      console.log('✅ تم إنشاء تقرير PDF بنجاح:', fileName);
    } catch (error) {
      console.error('❌ خطأ في إنشاء تقرير PDF:', error);
      alert('حدث خطأ في إنشاء التقرير. يرجى المحاولة مرة أخرى.');
    }
  };

  // تصدير البيانات إلى Excel
  const exportToExcel = () => {
    try {
      console.log('📊 بدء تصدير البيانات إلى Excel...');

      if (deviceLinks.length === 0) {
        alert('لا توجد بيانات للتصدير');
        return;
      }

      // إعداد البيانات للتصدير
      const exportData = deviceLinks.map(link => ({
        'رقم الطلب': `#${link.orderNumber}`,
        'العميل': link.customerName,
        'نوع الجهاز': link.deviceType,
        'حجم الجهاز': link.deviceSize || '',
        'الفني': link.technicianName,
        'نوع الراتب': getSalaryTypeLabel(link.salaryType),
        'تكلفة الصيانة': link.baseAmount,
        'مستحقات الفني': link.earningAmount,
        'تفاصيل الحساب': link.calculationDetails,
        'الحالة': getStatusLabel(link.status),
        'الفرع': link.branchName,
        'تاريخ الإنشاء': link.createdDate.toLocaleDateString('ar-EG'),
        'تاريخ الاكتمال': link.completedDate ? link.completedDate.toLocaleDateString('ar-EG') : ''
      }));

      // إضافة صف الإجمالي
      exportData.push({
        'رقم الطلب': '',
        'العميل': '',
        'نوع الجهاز': '',
        'حجم الجهاز': '',
        'الفني': '',
        'نوع الراتب': 'إجمالي',
        'تكلفة الصيانة': deviceLinks.reduce((sum, link) => sum + link.baseAmount, 0),
        'مستحقات الفني': totalEarnings,
        'تفاصيل الحساب': `${completedDevices} جهاز مكتمل`,
        'الحالة': '',
        'الفرع': '',
        'تاريخ الإنشاء': '',
        'تاريخ الاكتمال': ''
      });

      // إنشاء ورقة العمل
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // إنشاء كتاب العمل
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'تقرير الأجهزة والفنيين');

      // إضافة معلومات التقرير في ورقة منفصلة
      const reportInfo = [
        ['معلومات التقرير'],
        ['تاريخ التقرير', new Date().toLocaleDateString('ar-EG')],
        ['الفترة الزمنية', getTimeRangeLabel()],
        ['الفني المحدد', selectedTechnician === 'all' ? 'جميع الفنيين' :
          technicians.find(t => t.id === selectedTechnician)?.name || 'غير معروف'],
        ['الفرع المحدد', selectedBranch === 'all' ? 'جميع الفروع' :
          branches.find(b => b.id === selectedBranch)?.name || 'غير معروف'],
        ['فلتر الحالة', statusFilter === 'all' ? 'جميع الحالات' : getStatusLabel(statusFilter)],
        [''],
        ['الإحصائيات'],
        ['إجمالي الأجهزة', totalDevices],
        ['أجهزة مكتملة', completedDevices],
        ['أجهزة قيد التنفيذ', pendingDevices],
        ['إجمالي المستحقات', totalEarnings]
      ];

      const infoWorksheet = XLSX.utils.aoa_to_sheet(reportInfo);
      XLSX.utils.book_append_sheet(workbook, infoWorksheet, 'معلومات التقرير');

      // حفظ الملف
      const fileName = `technician-device-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      console.log('✅ تم تصدير البيانات إلى Excel بنجاح:', fileName);
    } catch (error) {
      console.error('❌ خطأ في تصدير البيانات إلى Excel:', error);
      alert('حدث خطأ في تصدير البيانات. يرجى المحاولة مرة أخرى.');
    }
  };

  // تصفية وترتيب البيانات
  const filteredDeviceLinks = deviceLinks
    .filter(link => {
      if (!searchQuery) return true;

      const query = searchQuery.toLowerCase();
      return (
        link.orderNumber.toString().includes(query) ||
        link.customerName.toLowerCase().includes(query) ||
        link.deviceType.toLowerCase().includes(query) ||
        link.technicianName.toLowerCase().includes(query) ||
        link.branchName.toLowerCase().includes(query) ||
        getStatusLabel(link.status).toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'orderNumber':
          aValue = a.orderNumber;
          bValue = b.orderNumber;
          break;
        case 'customerName':
          aValue = a.customerName.toLowerCase();
          bValue = b.customerName.toLowerCase();
          break;
        case 'earningAmount':
          aValue = a.earningAmount;
          bValue = b.earningAmount;
          break;
        case 'createdDate':
          aValue = a.createdDate.getTime();
          bValue = b.createdDate.getTime();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

  // دالة تغيير الترتيب
  const handleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // عرض شاشة التحميل الأولي
  if (loadingInitial) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px">
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            جاري تحميل البيانات الأولية...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            يرجى الانتظار قليلاً
          </Typography>
        </Box>
      </Container>
    );
  }

  // عرض رسالة عدم وجود بيانات
  if (!loadingInitial && technicians.length === 0 && branches.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="warning" sx={{ mb: 3 }}>
          لا توجد بيانات متاحة. يرجى التأكد من وجود فنيين وفروع في النظام.
        </Alert>
        <Box display="flex" justifyContent="center">
          <Button variant="contained" onClick={loadInitialData}>
            إعادة المحاولة
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <AssessmentIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            تقرير ربط الأجهزة بالفنيين
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            طباعة
          </Button>
          <Button
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={generatePDFReport}
            disabled={loading || deviceLinks.length === 0}
            color="error"
          >
            تقرير PDF
          </Button>
          <Button
            variant="contained"
            startIcon={<ExcelIcon />}
            onClick={exportToExcel}
            disabled={loading || deviceLinks.length === 0}
            color="success"
          >
            تصدير Excel
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            فلاتر التقرير
          </Typography>

          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>الفني</InputLabel>
                <Select
                  value={selectedTechnician}
                  onChange={(e) => setSelectedTechnician(e.target.value)}
                  label="الفني"
                >
                  <MenuItem value="all">جميع الفنيين</MenuItem>
                  {technicians.map((technician) => (
                    <MenuItem key={technician.id} value={technician.id}>
                      <Box display="flex" alignItems="center">
                        <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                          <EngineeringIcon sx={{ fontSize: 14 }} />
                        </Avatar>
                        {technician.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>الفرع</InputLabel>
                <Select
                  value={selectedBranch}
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  label="الفرع"
                >
                  <MenuItem value="all">جميع الفروع</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="الحالة"
                >
                  <MenuItem value="all">جميع الحالات</MenuItem>
                  <MenuItem value="completed">مكتمل</MenuItem>
                  <MenuItem value="delivered">تم التسليم</MenuItem>
                  <MenuItem value="in_progress">قيد التنفيذ</MenuItem>
                  <MenuItem value="pending">في الانتظار</MenuItem>
                  <MenuItem value="returned">مرتجع</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>الفترة الزمنية</InputLabel>
                <Select
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value as TimeRange)}
                  label="الفترة الزمنية"
                >
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="week">آخر أسبوع</MenuItem>
                  <MenuItem value="month">آخر شهر</MenuItem>
                  <MenuItem value="year">آخر سنة</MenuItem>
                  <MenuItem value="custom">فترة مخصصة</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {timeRange === 'custom' && (
              <>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="من تاريخ"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    label="إلى تاريخ"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="البحث السريع"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="رقم الطلب، العميل، الفني..."
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSelectedTechnician('all');
                  setSelectedBranch('all');
                  setStatusFilter('all');
                  setSearchQuery('');
                  setSortBy('orderNumber');
                  setSortOrder('desc');
                  setTimeRange('month');
                  setCustomStartDate('');
                  setCustomEndDate('');
                }}
                sx={{ height: '56px' }}
              >
                إعادة تعيين الفلاتر
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <DevicesIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{totalDevices}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي الأجهزة المربوطة
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <MoneyIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">
                    {formatCurrency(totalEarnings)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    إجمالي المستحقات ({getTimeRangeLabel()})
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{completedDevices}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    أجهزة مكتملة
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <AssessmentIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{pendingDevices}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    أجهزة قيد التنفيذ
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Financial Summary Card */}
      {selectedTechnician !== 'all' && deviceLinks.length > 0 && (
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom color="primary.main" fontWeight="bold">
              <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ملخص مالي للفني: {technicians.find(t => t.id === selectedTechnician)?.name}
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h4" color="success.main" fontWeight="bold">
                    {formatCurrency(totalEarnings)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    إجمالي المستحقات ({getTimeRangeLabel()})
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" color="primary.main" fontWeight="bold">
                    {completedDevices}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    أجهزة مكتملة ومستحقة الدفع
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box textAlign="center" p={2}>
                  <Typography variant="h5" color="warning.main" fontWeight="bold">
                    {pendingDevices}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    أجهزة قيد التنفيذ (غير مستحقة بعد)
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {completedDevices > 0 && (
              <Box mt={2} p={2} bgcolor="success.50" borderRadius={1}>
                <Typography variant="body2" color="success.dark" textAlign="center">
                  💰 متوسط المستحقات لكل جهاز مكتمل: {formatCurrency(totalEarnings / completedDevices)}
                </Typography>
              </Box>
            )}

            {/* تفاصيل إضافية للفني المحدد */}
            {selectedTechnician !== 'all' && deviceLinks.length > 0 && (
              <Box mt={2}>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      📊 توزيع الأجهزة حسب النوع:
                    </Typography>
                    {Object.entries(
                      deviceLinks.reduce((acc, link) => {
                        acc[link.deviceType] = (acc[link.deviceType] || 0) + 1;
                        return acc;
                      }, {} as Record<string, number>)
                    ).map(([deviceType, count]) => (
                      <Typography key={deviceType} variant="caption" display="block">
                        • {deviceType}: {count} جهاز
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      💰 توزيع المستحقات حسب نوع الراتب:
                    </Typography>
                    {Object.entries(
                      deviceLinks
                        .filter(link => link.status === 'completed' || link.status === 'delivered')
                        .reduce((acc, link) => {
                          const salaryType = getSalaryTypeLabel(link.salaryType);
                          acc[salaryType] = (acc[salaryType] || 0) + link.earningAmount;
                          return acc;
                        }, {} as Record<string, number>)
                    ).map(([salaryType, amount]) => (
                      <Typography key={salaryType} variant="caption" display="block">
                        • {salaryType}: {formatCurrency(amount)}
                      </Typography>
                    ))}
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Device Links Table */}
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              تفاصيل ربط الأجهزة بالفنيين ({getTimeRangeLabel()})
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                {filteredDeviceLinks.length} من {deviceLinks.length} جهاز مربوط
                {searchQuery && ` (مفلتر: "${searchQuery}")`}
              </Typography>
              {totalEarnings > 0 && (
                <Chip
                  label={`إجمالي المستحقات: ${formatCurrency(totalEarnings)}`}
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : filteredDeviceLinks.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('orderNumber')}
                    >
                      <Box display="flex" alignItems="center">
                        رقم الطلب
                        {sortBy === 'orderNumber' && (
                          sortOrder === 'asc' ? <ArrowUpIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('customerName')}
                    >
                      <Box display="flex" alignItems="center">
                        العميل
                        {sortBy === 'customerName' && (
                          sortOrder === 'asc' ? <ArrowUpIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>نوع الجهاز</TableCell>
                    <TableCell>الفني</TableCell>
                    <TableCell>نوع الراتب</TableCell>
                    <TableCell>تكلفة الصيانة</TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('earningAmount')}
                    >
                      <Box display="flex" alignItems="center">
                        مستحقات الفني
                        {sortBy === 'earningAmount' && (
                          sortOrder === 'asc' ? <ArrowUpIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>الحالة</TableCell>
                    <TableCell>الفرع</TableCell>
                    <TableCell
                      sx={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('createdDate')}
                    >
                      <Box display="flex" alignItems="center">
                        تاريخ الإنشاء
                        {sortBy === 'createdDate' && (
                          sortOrder === 'asc' ? <ArrowUpIcon sx={{ ml: 1, fontSize: 16 }} /> : <ArrowDownIcon sx={{ ml: 1, fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>الإجراءات</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDeviceLinks.map((link) => (
                    <TableRow key={link.maintenanceId}>
                      <TableCell>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          #{link.orderNumber}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {link.customerName}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {link.deviceType}
                          </Typography>
                          {link.deviceSize && (
                            <Typography variant="caption" color="text.secondary">
                              {link.deviceSize}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: 'primary.main' }}>
                            <EngineeringIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Typography variant="body2">
                            {link.technicianName}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={getSalaryTypeLabel(link.salaryType)}
                          color={getSalaryTypeColor(link.salaryType)}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(link.baseAmount)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {formatCurrency(link.earningAmount)}
                          </Typography>
                          <Tooltip title={link.calculationDetails}>
                            <Typography variant="caption" color="text.secondary">
                              {link.calculationDetails.length > 20
                                ? link.calculationDetails.substring(0, 20) + '...'
                                : link.calculationDetails
                              }
                            </Typography>
                          </Tooltip>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Tooltip title={getStatusTooltip(link.status)} placement="top">
                          <Chip
                            label={getStatusLabel(link.status)}
                            color={getStatusColor(link.status)}
                            size="small"
                          />
                        </Tooltip>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {link.branchName}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {link.createdDate.toLocaleDateString('ar-EG')}
                        </Typography>
                        {link.completedDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            اكتمل: {link.completedDate.toLocaleDateString('ar-EG')}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Tooltip title="عرض تفاصيل الطلب">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`/admin/maintenance/${link.maintenanceId}`, '_blank')}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : deviceLinks.length > 0 ? (
            <Alert severity="info">
              لا توجد نتائج تطابق البحث "{searchQuery}". جرب تغيير كلمات البحث.
            </Alert>
          ) : (
            <Alert severity="info">
              لا توجد أجهزة مربوطة بفنيين في الفترة المحددة.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default TechnicianDeviceReportPage;
